import { useRef, useState } from "react";
import { toast } from "sonner";
import { Check, ImagePlus, Loader2, CreditCard, AlertTriangle, Sparkles } from "lucide-react";
import { api, apiError, mediaUrl } from "@/lib/api";

const DURATIONS = [30, 45, 60];
const field = "w-full rounded-lg border border-[#ccd0d5] bg-white px-3 py-2 text-sm outline-none focus:border-[#1877f2]";

function PhotoGrid({ spot, setSpot }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const toggle = async (path) => {
    const selected = spot.photos.filter(p => (p.path === path ? !p.selected : p.selected)).map(p => p.path);
    try { setSpot(await api.spotUpdate(spot.id, { selected })); } catch (e) { toast.error(apiError(e)); }
  };
  const upload = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    setUploading(true);
    try {
      for (const f of files) setSpot(await api.spotAddPhoto(spot.id, f));
    } catch (err) { toast.error(apiError(err)); } finally { setUploading(false); }
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold uppercase text-[#65676b]">Photos du spot ({spot.photos.filter(p => p.selected).length} gardées)</span>
        <input ref={fileRef} type="file" multiple accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} data-testid="spot-photo-input" />
        <button type="button" className="btn-ghost !py-1.5" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="spot-add-photo-btn">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} Ajouter mes photos
        </button>
      </div>
      {spot.photos.length === 0 ? (
        <p className="text-sm text-[#65676b] rounded-lg bg-[#f0f2f5] p-4" data-testid="spot-no-photos">Aucune photo trouvée : ajoutez vos propres photos.</p>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="spot-photos">
          {spot.photos.map((p, i) => (
            <button type="button" key={p.path} onClick={() => toggle(p.path)} data-testid={`spot-photo-${i}`}
              className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${p.selected ? "border-[#1877f2]" : "border-transparent opacity-40 grayscale"}`}>
              <img src={mediaUrl(p.url)} alt="" className="w-full h-full object-cover" />
              {p.selected && <span className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-[#1877f2] grid place-items-center"><Check className="w-4 h-4 text-white" /></span>}
            </button>
          ))}
        </div>
      )}
      <p className="text-xs text-[#8a8d91] mt-2">Touchez une photo pour la retirer ou la remettre.</p>
    </div>
  );
}

export function SpotEditor({ spot, setSpot, isAdmin }) {
  const [form, setForm] = useState({ site_name: spot.site_name || "", slogan: spot.slogan || "", description: spot.description || "", cta: spot.cta || "" });
  const [paying, setPaying] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async (extra = {}) => setSpot(await api.spotUpdate(spot.id, { ...form, ...extra }));

  const pay = async () => {
    setPaying(true);
    try {
      await save();
      const res = await api.spotCheckout(spot.id, window.location.origin);
      if (res.checkout_url) { window.location.href = res.checkout_url; return; }
      toast.success("Spot lancé — il sera prêt dans ~1 min dans la galerie");
      setSpot(await api.spotGet(spot.id));
    } catch (e) { toast.error(apiError(e)); } finally { setPaying(false); }
  };

  return (
    <div className="card-surface p-6 space-y-5" data-testid="spot-editor">
      {spot.warning && <p className="flex gap-2 text-sm rounded-lg bg-[#fff7e6] px-4 py-3" data-testid="spot-warning"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-500" />{spot.warning}</p>}
      <div className="flex items-center gap-4">
        {spot.logo_url ? <img src={mediaUrl(spot.logo_url)} alt="logo" className="w-16 h-16 object-contain rounded-xl border border-[#dddfe2] bg-white p-1.5" data-testid="spot-logo" />
          : <span className="w-16 h-16 rounded-xl bg-[#f0f2f5] grid place-items-center text-xs text-[#65676b] text-center" data-testid="spot-no-logo">Pas de logo</span>}
        <a href={spot.url} target="_blank" rel="noreferrer" className="text-sm text-[#1877f2] font-semibold break-all" data-testid="spot-url">{spot.url}</a>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block"><span className="text-xs font-semibold uppercase text-[#65676b]">Nom de la marque</span>
          <input className={`${field} mt-1`} maxLength={60} value={form.site_name} onChange={e => set("site_name", e.target.value)} data-testid="spot-name-input" /></label>
        <label className="block"><span className="text-xs font-semibold uppercase text-[#65676b]">Appel à l'action (facultatif)</span>
          <input className={`${field} mt-1`} maxLength={40} placeholder="Ex : Réservez votre table" value={form.cta} onChange={e => set("cta", e.target.value)} data-testid="spot-cta-input" /></label>
      </div>
      <label className="block"><span className="text-xs font-semibold uppercase text-[#65676b]">Slogan</span>
        <input className={`${field} mt-1`} maxLength={140} value={form.slogan} onChange={e => set("slogan", e.target.value)} data-testid="spot-slogan-input" /></label>
      <label className="block"><span className="text-xs font-semibold uppercase text-[#65676b]">Description / ce qu'il faut mettre en avant</span>
        <textarea rows={3} className={`${field} mt-1 resize-none`} maxLength={600} value={form.description} onChange={e => set("description", e.target.value)} data-testid="spot-description-input" /></label>
      <PhotoGrid spot={spot} setSpot={setSpot} />
      <div>
        <span className="text-xs font-semibold uppercase text-[#65676b]">Durée du spot (vertical 9:16)</span>
        <div className="flex gap-2 mt-2">
          {DURATIONS.map(d => (
            <button key={d} type="button" onClick={() => save({ duration: d }).catch(e => toast.error(apiError(e)))} data-testid={`spot-duration-${d}`}
              className={`flex-1 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${spot.duration === d ? "border-[#1877f2] bg-[#e7f3ff] text-[#1877f2]" : "border-[#dddfe2] text-[#65676b] hover:border-[#bcc0c4]"}`}>
              {d} s
            </button>
          ))}
        </div>
      </div>
      <button type="button" className="btn-primary w-full justify-center !py-3.5 !text-base" disabled={paying} onClick={pay} data-testid="spot-pay-btn">
        {paying ? <Loader2 className="w-5 h-5 animate-spin" /> : isAdmin ? <Sparkles className="w-5 h-5" /> : <CreditCard className="w-5 h-5" />}
        {isAdmin ? "Créer le spot (gratuit admin)" : "Payer 5 € et créer le spot"}
      </button>
      {!isAdmin && <p className="text-xs text-center text-[#8a8d91]">Paiement unique sécurisé par Stripe · ne compte pas dans vos 30 vidéos du mois · relance gratuite en cas d'échec</p>}
    </div>
  );
}
